import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { set } from 'react-hook-form';

export type AuthModalView = 'signin' | 'signup' | 'forgot' | 'otp' | 'newPassword' | 'multipleUsers' | 'profileSetup' | 'closed';

export interface AuthModalState {
  isOpen: boolean;
  view: AuthModalView;
  emailForReset?: string;
  signupEmail?: string;
  signupUserId?: string;
  signupMobileNumber?: string;
  signupCountryCode?: number;
  /** Password for signup OTP flow only; cleared after InitiateAuth. */
  signupPassword?: string;
  verificationCode?: string;
  /** Set after forgot-password OTP is validated via Cognito; used only for InitiateAuth + ChangePassword on New Password step. */
  forgotTempPassword?: string;
  signinPassword?: string; // Temporary password storage for signin confirmation flow
  isGuestMode?: boolean; // Guest mode flag
  isForgotUsername?: boolean; // Flag to track if coming from Forgot Username
  usernames?: string[]; // Store usernames for forgot username flow
  prefilledUsername?: string; // Store username to pre-fill in login form
  playerId?:string;

}

const initialState: AuthModalState = {
  isOpen: false,
  view: 'closed',
  emailForReset: undefined,
  signupEmail: undefined,
  signupUserId: undefined,
  signupMobileNumber: undefined,
  signupCountryCode: undefined,
  signupPassword: undefined,
  verificationCode: undefined,
  forgotTempPassword: undefined,
  signinPassword: undefined,
  isGuestMode: false,
  isForgotUsername: false,
  usernames: undefined,
  prefilledUsername: undefined,
  playerId: undefined,
};

const authModalSlice = createSlice({
  name: 'authModal',
  initialState,
  reducers: {
    openAuth(state, action: PayloadAction<AuthModalView | undefined>) {
      state.isOpen = true;
      state.view = action.payload ?? 'signin';
    },
    closeAuth() {
      return initialState;
    },
    setAuthView(state, action: PayloadAction<AuthModalView>) {
      state.view = action.payload;
      state.isOpen = action.payload === 'closed' ? false : true;
    },
    setResetEmail(state, action: PayloadAction<string | undefined>) {
      state.emailForReset = action.payload;
    },
    setSignupData(state, action: PayloadAction<{ email: string; userId: string, password: string, mobileNumber: string } | undefined>) {
      state.signupEmail = action.payload?.email;
      state.signupUserId = action.payload?.userId;
      state.signupPassword = action.payload?.password;
      state.signupMobileNumber = action.payload?.mobileNumber;
    },
    setVerificationCode(state, action: PayloadAction<string | undefined>) {
      state.verificationCode = action.payload;
    },
    setForgotTempPassword(state, action: PayloadAction<string | undefined>) {
      state.forgotTempPassword = action.payload;
    },
    setSigninPassword(state, action: PayloadAction<string | undefined>) {
      state.signinPassword = action.payload;
    },
    setGuestMode(state, action: PayloadAction<boolean>) {
      state.isGuestMode = action.payload;
    },
    setForgotUsername(state, action: PayloadAction<boolean>) {
      state.isForgotUsername = action.payload;
    },
    setUsernames(state, action: PayloadAction<string[] | undefined>) {
      state.usernames = action.payload;
    },
    setPrefilledUsername(state, action: PayloadAction<string | undefined>) {
      state.prefilledUsername = action.payload;
    },
    setPlayerId(state, action: PayloadAction<string | undefined>) {
      state.playerId = action.payload;
    }
  },
});

export const {
  openAuth,
  closeAuth,
  setAuthView,
  setResetEmail,
  setSignupData,
  setVerificationCode,
  setForgotTempPassword,
  setSigninPassword,
  setGuestMode,
  setForgotUsername,
  setUsernames,
  setPrefilledUsername,
  setPlayerId,
} = authModalSlice.actions;
export default authModalSlice.reducer;


